// My Profile — every user sees + edits their own AttorneyProfile
// (Phase 4-μ). Contact + identity fields that feed document generation:
// bar_id, initials, direct phone/fax, personal address block. Auto-
// populated fields (name, email) come from Clio's who_am_i and are
// shown read-only.
//
// Writing style + workflow preferences + signature block credentials
// are Phase 5 concerns — this page is the identity-only surface for now.

import React, { useEffect, useState } from "react";
import { C } from "../constants.js";
import { apiFetch } from "../api.js";

export function MyProfilePage({ authStatus, showNotification }) {
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  // Substep 5-κ.3: locations for the office dropdown.
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    Promise.all([
      apiFetch("/api/my-profile"),
      apiFetch("/api/firm-locations?active_only=true").catch(() => ({ data: [] })),
    ])
      .then(([p, locs]) => {
        setProfile(p);
        setForm({
          initials: p.initials || "",
          bar_id: p.bar_id || "",
          direct_phone: p.direct_phone || "",
          fax: p.fax || "",
          address: p.address || "",
          city_state_zip: p.city_state_zip || "",
          location_id: p.location_id || "",
        });
        setLocations(locs.data || []);
        setDirty(false);
      })
      .catch(e => showNotification?.(`Profile load failed: ${e.message}`, "error"));
  }, [showNotification]);

  const update = (field) => (e) => {
    setForm(f => ({ ...f, [field]: e.target.value }));
    setDirty(true);
  };

  const save = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      // location_id serializes as int (or null when unset) — the "" from
      // an unselected dropdown would fail the backend Optional[int] check.
      const payload = { ...form };
      payload.location_id = form.location_id === "" || form.location_id == null
        ? null : parseInt(form.location_id, 10);
      await apiFetch("/api/my-profile", {
        method: "PUT",
        body: JSON.stringify(payload),
      });
      // Merge form into profile so the read-only pane reflects the save.
      setProfile(p => ({ ...(p || {}), ...form, location_id: payload.location_id }));
      setDirty(false);
      showNotification?.("Profile saved.", "success");
    } catch (e) {
      showNotification?.(`Save failed: ${e.message}`, "error");
    }
    setSaving(false);
  };

  if (!profile || !form) {
    return React.createElement("div", { style: { padding: "40px", color: C.textMuted } },
      "Loading your profile…"
    );
  }

  return React.createElement("div", { className: "fade-in", style: { padding: "32px 40px", maxWidth: "900px" } },
    // ─── Header ─────────────────────────────────────────────────
    React.createElement("div", {
      style: { marginBottom: "24px", display: "flex", justifyContent: "space-between", alignItems: "flex-end" }
    },
      React.createElement("div", null,
        React.createElement("h1", {
          style: { fontFamily: "'Playfair Display',serif", fontSize: "28px", fontWeight: 600, color: C.navy, marginBottom: "6px" }
        }, "My Profile"),
        React.createElement("p", { style: { fontSize: "14px", color: C.textMuted } },
          "Your contact and letterhead info. Populates letters, LORs, and other generated documents you're the sender on."
        ),
      ),
      React.createElement("button", {
        onClick: save,
        disabled: !dirty || saving,
        style: {
          padding: "10px 20px", borderRadius: "8px", border: "none",
          background: (!dirty || saving) ? C.warmGray : C.gold,
          color: "#fff", fontSize: "14px", fontWeight: 600,
          cursor: (!dirty || saving) ? "default" : "pointer",
        }
      }, saving ? "Saving…" : dirty ? "Save changes" : "Saved"),
    ),

    // ─── Identity (read-only, from Clio) ────────────────────────
    _section("Identity (from Clio)",
      React.createElement("p", {
        style: { fontSize: "12px", color: C.textMuted, marginBottom: "10px" }
      }, "Auto-populated on login from your Clio profile. Edit these in Clio if they're wrong."),
      _field("Full name", null, _readOnly(profile.name)),
      _field("Email", null, _readOnly(profile.email)),
    ),

    // ─── Editable contact + letterhead ──────────────────────────
    _section("Contact & letterhead",
      // Substep 5-κ.3: office location. Documents I generate use this
      // location's letterhead (address, phone, fax, email).
      _field("Office location",
        "Which office you sit at. Documents you generate use this office's letterhead (address, phone, fax, email). Firm admin manages the list in Firm Settings.",
        React.createElement("select", {
          value: form.location_id || "",
          onChange: (e) => { setForm(f => ({ ...f, location_id: e.target.value })); setDirty(true); },
          style: {
            width: "100%", padding: "8px 10px", border: `1px solid ${C.warmGray}`,
            borderRadius: "6px", fontSize: "13px", boxSizing: "border-box",
            background: "#fff", color: C.textDark,
          },
        },
          React.createElement("option", { value: "" }, "(none — falls back to firm default)"),
          ...locations.map(loc => React.createElement("option", { key: loc.id, value: loc.id },
            loc.name + (loc.bar_states ? ` — ${loc.bar_states}` : "")
              + (loc.is_default ? "  (firm default)" : ""),
          )),
        ),
      ),
      _field("Initials", "Shown in some document footers and email signatures (e.g., 'ABM').",
        _textInput(form.initials, update("initials"), 10),
      ),
      _field("Bar ID", "State bar registration number. Appears in your signature block on court filings.",
        _textInput(form.bar_id, update("bar_id"), 50),
      ),
      _field("Direct phone", "Your direct dial. If empty, generated docs fall back to the firm's primary phone.",
        _textInput(form.direct_phone, update("direct_phone"), 50),
      ),
      _field("Fax", "Personal or firm fax for records requests + court filings.",
        _textInput(form.fax, update("fax"), 50),
      ),
    ),

    // ─── Address block ──────────────────────────────────────────
    _section("Address block",
      React.createElement("p", {
        style: { fontSize: "12px", color: C.textMuted, marginBottom: "10px" }
      }, "Only fill in if it differs from the firm's main letterhead address (e.g., you work from a satellite office)."),
      _field("Street address", null,
        _textInput(form.address, update("address"), 500),
      ),
      _field("City, state, zip", null,
        _textInput(form.city_state_zip, update("city_state_zip"), 200),
      ),
    ),

    // ─── Coming later (Phase 5) placeholder ─────────────────────
    _section("Coming later",
      React.createElement("ul", {
        style: { fontSize: "13px", color: C.textMuted, lineHeight: 1.7, margin: 0, paddingLeft: "20px" }
      },
        React.createElement("li", null, "Signature block — credentials suffix (JD, Esq.), professional titles, bar memberships"),
        React.createElement("li", null, "AI writing style — a per-attorney tone hint the drafting AI mirrors when it writes on your behalf"),
        React.createElement("li", null, "Workflow preferences — default assignees on new tasks, notification opt-ins"),
      ),
    ),
  );
}


// ─── Small helpers (mirrors FirmSettingsPage patterns) ──────────

function _section(title, ...children) {
  return React.createElement("div", {
    style: {
      background: "#fff", borderRadius: "12px", padding: "20px 24px",
      border: `1px solid ${C.warmGray}`, marginBottom: "20px",
    }
  },
    React.createElement("h2", {
      style: {
        fontSize: "13px", color: C.textMuted, textTransform: "uppercase",
        letterSpacing: "0.08em", fontWeight: 600, margin: "0 0 16px 0",
      }
    }, title),
    ...children,
  );
}

function _field(label, hint, control) {
  return React.createElement("div", { style: { marginBottom: "16px" } },
    React.createElement("label", {
      style: { display: "block", fontSize: "13px", fontWeight: 600, color: C.navy, marginBottom: "4px" }
    }, label),
    hint && React.createElement("div", {
      style: { fontSize: "12px", color: C.textMuted, marginBottom: "6px" }
    }, hint),
    control,
  );
}

function _textInput(value, onChange, maxLength) {
  return React.createElement("input", {
    type: "text", value, onChange, maxLength,
    style: {
      width: "100%", padding: "8px 10px", border: `1px solid ${C.warmGray}`,
      borderRadius: "6px", fontSize: "13px", boxSizing: "border-box",
    }
  });
}

function _readOnly(value) {
  return React.createElement("div", {
    style: {
      padding: "8px 10px", border: `1px solid ${C.warmGray}`,
      borderRadius: "6px", fontSize: "13px", boxSizing: "border-box",
      background: C.warmWhite, color: C.textMuted,
    }
  }, value || React.createElement("em", null, "(not set)"));
}
